import type { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import type { PortalUser } from "@/app/models";
import { trackPrivateEvent } from "@/features/analytics/api";
import {
  CUSTOMER_IO_CATEGORY_SLUG,
  CUSTOMER_IO_EVENT,
} from "@/features/analytics/models";
import { hasWeightLossProduct } from "@/features/analytics/utils/customer-io";
import type { OnboardingUser } from "@/features/onboarding/models";
import type { Product } from "@/features/products/models";
import type { Category, Coupon, Price } from "@/shared/domains";
import type { AvailableLanguage } from "@/shared/models";
import type { Patient, USState } from "@/shared/models/legacy-types";
import { parsePhoneNumber } from "@/shared/utils";

interface CustomerIoShopPageViewedParams {
  analytics: AnalyticsBrowser;
  userInfo: {
    email: PortalUser["email"];
    firstName: Patient["firstName"];
    lastName: Patient["lastName"];
    dateOfBirth: Patient["dateOfBirth"];
    gender: PortalUser["gender"];
    phone: Patient["phoneNumber"];
    state?: USState["value"];
  };
  language: AvailableLanguage;
}

export const triggerCustomerIoShopPageViewed = ({
  analytics,
  userInfo,
  language,
}: CustomerIoShopPageViewedParams) => {
  void analytics.identify(userInfo.email, {
    ...userInfo,
    phone: parsePhoneNumber(userInfo.phone ?? ""),
    source: window.location.href,
    language,
  });

  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.SHOP_PAGE_VIEWED,
    userId: userInfo.email,
  });
};

interface CustomerIoProductClickedParams {
  product: Product;
}

export const triggerCustomerIoProductClicked = ({
  product,
}: CustomerIoProductClickedParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.PRODUCT_SELECTED,
    properties: {
      productCategory: CUSTOMER_IO_CATEGORY_SLUG[product.category.slug],
      productName: product.slug,
      source: window.location.href,
    },
  });
};

interface CustomerIoShopSummaryPageViewedParams {
  product: Product;
}

export const triggerCustomerIoShopSummaryPageViewed = ({
  product,
}: CustomerIoShopSummaryPageViewedParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.SHOP_SUMMARY_PAGE_VIEWED,
    properties: {
      productCategory: CUSTOMER_IO_CATEGORY_SLUG[product.category.slug],
      productName: product.slug,
      source: window.location.href,
    },
  });
};

interface BaseCustomerIoShopPaymentResultParams {
  user: PortalUser;
  totalPrice: number;
  discount?: Coupon | null;
}

interface FailedCustomerIoShopPaymentResultParams
  extends BaseCustomerIoShopPaymentResultParams {
  productId: Product["id"];
}

export const triggerCustomerIoShopPaymentFailed = ({
  totalPrice,
  discount,
  productId,
}: FailedCustomerIoShopPaymentResultParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.PAYMENT_FAILED,
    properties: {
      productId,
      totalPrice,
      discountCode: discount?.code,
      source: window.location.href,
    },
  });
};

interface CompletedCustomerIoShopPaymentResultParams
  extends BaseCustomerIoShopPaymentResultParams {
  products: {
    productCategory?: Category["slug"];
    productName?: Product["slug"];
    productId: Price["subscriptionPriceId"];
    productFrequency: Price["durationInMonths"];
    price: Price["price"];
  }[];
  succeededSubscriptionIds: string[];
}

export const triggerCustomerIoShopPaymentCompleted = ({
  products,
  totalPrice,
  discount,
  succeededSubscriptionIds,
}: CompletedCustomerIoShopPaymentResultParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.PAYMENT_COMPLETED,
    properties: {
      succeededSubscriptionIds,
      products,
      hasWeightLossProducts: hasWeightLossProduct(products),
      amountPaid: totalPrice,
      discountCode: discount?.code,
      source: window.location.href,
    },
  });
};

interface CustomerIoPortalEventParams {
  userEmail: OnboardingUser["email"];
}

export const triggerCustomerIoUserLoggedOut = ({
  userEmail,
}: CustomerIoPortalEventParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.USER_LOGGED_OUT,
    properties: { source: window.location.href },
    userId: userEmail,
  });
};

export const triggerCustomerIoUserProfileUpdated = ({
  userEmail,
}: CustomerIoPortalEventParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.USER_PROFILE_UPDATED,
    properties: { source: window.location.href },
    userId: userEmail,
  });
};

export const triggerCustomerIoUserLoggedIn = ({
  userEmail,
}: CustomerIoPortalEventParams) => {
  trackPrivateEvent({
    event: CUSTOMER_IO_EVENT.USER_LOGGED_IN,
    properties: { source: window.location.href },
    userId: userEmail,
  });
};
